import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, FileText, Paperclip, X, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const uploadFile = async (kind, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/uploads/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

const cache = {};
const blobOf = (path) => (cache[path] ||= api.get("/uploads/file", { params: { path }, responseType: "blob" }).then((r) => URL.createObjectURL(r.data)));

export const StoredImg = ({ path, className, testId }) => {
  const [src, setSrc] = useState("");
  useEffect(() => { if (path) blobOf(path).then(setSrc).catch(() => setSrc("")); }, [path]);
  return src ? <img src={src} alt="" className={className} data-testid={testId} /> : null;
};

export const openStored = (path) => {
  const w = window.open("", "_blank");
  blobOf(path).then((u) => { if (w) w.location.href = u; });
};

const Picker = ({ kind, accept, onDone, children, testId, className, variant = "outline" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { onDone(await uploadFile(kind, f)); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      <Button type="button" size="sm" variant={variant} className={className} disabled={busy} onClick={() => ref.current.click()} data-testid={testId}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : children}
      </Button>
    </>
  );
};

export const PhotoField = ({ value, onChange, disabled, testId }) => (
  <div className="flex items-center gap-1.5">
    {value ? (
      <span className="relative">
        <StoredImg path={value} className="h-10 w-10 cursor-pointer rounded border border-slate-200 object-cover" testId={`${testId}-thumb`} />
        {!disabled && <button type="button" className="absolute -right-1.5 -top-1.5 rounded-full bg-rose-500 p-0.5 text-white" onClick={() => onChange("")} data-testid={`${testId}-remove`}><X className="h-2.5 w-2.5" /></button>}
      </span>
    ) : !disabled && (
      <Picker kind="photo" accept="image/png,image/jpeg" onDone={(r) => onChange(r.path)} testId={testId} variant="ghost" className="h-7 px-2 text-[11px] text-slate-500"><Camera className="h-3.5 w-3.5 mr-1" />Foto (opsional)</Picker>
    )}
  </div>
);

export const AttachmentsPanel = ({ value = [], onChange, disabled, testPrefix = "attach" }) => (
  <div className="card-premium p-6 fade-up" data-testid={`${testPrefix}-panel`}>
    <div className="mb-3 flex items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><Paperclip className="h-5 w-5 text-blue-800" />Lampiran PDF (Data Sheet / Brosur)</h3>
      {!disabled && <Picker kind="doc" accept="application/pdf" onDone={(r) => onChange([...value, r])} testId={`${testPrefix}-add-btn`} className="bg-white"><Paperclip className="h-4 w-4 mr-1" />Tambah PDF</Picker>}
    </div>
    {!value.length ? <p className="text-sm text-slate-400">Belum ada lampiran. Lampiran akan digabung di belakang PDF penawaran & ikut terkirim via email.</p> : (
      <ul className="space-y-2">
        {value.map((a, i) => (
          <li key={a.path} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" data-testid={`${testPrefix}-item-${i}`}>
            <button type="button" className="flex items-center gap-2 text-left text-blue-800 hover:underline" onClick={() => openStored(a.path)}><FileText className="h-4 w-4 text-rose-600" />{a.name}</button>
            <span className="flex items-center gap-2 text-xs text-slate-400">{a.size ? `${(a.size / 1024 / 1024).toFixed(2)} MB` : ""}
              {!disabled && <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`${testPrefix}-remove-${i}`}><X className="h-4 w-4 text-rose-500" /></button>}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const BrochureField = ({ path, name, onChange, testId }) => (
  <div className="flex items-center gap-2">
    {path ? <><button type="button" className="text-sm text-blue-800 underline" onClick={() => openStored(path)}>{name || "Brosur.pdf"}</button><button type="button" onClick={() => onChange({ brochure_path: "", brochure_name: "" })} data-testid={`${testId}-remove`}><X className="h-4 w-4 text-rose-500" /></button></>
      : <Picker kind="doc" accept="application/pdf" onDone={(r) => onChange({ brochure_path: r.path, brochure_name: r.name })} testId={testId} className="bg-white"><Paperclip className="h-4 w-4 mr-1" />Upload PDF</Picker>}
  </div>
);
